import { useState, useCallback, useMemo, useRef, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, message, Modal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    BackgroundVariant,
    useNodesState,
    useEdgesState,
    useOnSelectionChange,
    ConnectionLineType,
} from '@xyflow/react';
import Sidebar from './Sidebar';
import ProjectHeader from './components/ProjectHeader';
import ZoomToolbar from './components/ZoomToolbar';
import CanvasToolbar from './components/CanvasToolbar';
import CanvasEmpty from './components/CanvasEmpty';
import CanvasContextMenu from './components/CanvasContextMenu';
import GroupActionToolbar from './components/GroupActionToolbar';
import GenerationHistoryModal from './components/GenerationHistoryModal';
import MaterialLibraryModal from './components/MaterialLibraryModal';
import TemplateLibraryModal from './components/TemplateLibraryModal';
import PendingConnectionNode from './components/PendingConnectionNode';
import { ImageNode, FunctionNode, GroupNode, FlowEdge } from './shapes';
import {
    batchUpdateFlowCanvas,
    getFlowProjectDetail,
} from './execution';
import {
    UPLOAD_IMAGE_ACCEPT,
    PENDING_CONNECTION_NODE_ID,
    PENDING_CONNECTION_EDGE_ID,
    DEFAULT_VIEWPORT,
    RUN_GROUP_SKIP_SELECT_STORAGE_KEY,
    SKIP_AUTO_SELECT_QUERY_KEY,
    CREATE_PROJECT_QUERY_KEY,
    TARGET_PROJECT_ID_QUERY_KEY,
    TARGET_PROJECT_NAME_QUERY_KEY,
    TARGET_EXECUTION_ID_QUERY_KEY,
    PM_VIEW_QUERY_KEY,
    FORCE_PERSONAL_MEMBER_QUERY_KEY,
    SOURCE_PAGE_QUERY_KEY,
    SOURCE_TAB_QUERY_KEY,
    RETURN_TO_QUERY_KEY,
    FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY,
    DEFAULT_CANVAS_PROJECT_NAME,
    CANVAS_VERSION_CONFLICT_MESSAGE,
} from './constants/canvas';
import {
    normalizeCreativeBootstrapPayload,
    buildCreativeBootstrapNodePatch,
    getErrorMessage,
    isCanvasVersionConflictError,
    getVersionNumber,
    getStoredLastActiveProject,
} from './utils/common';
import {
    sortNodesByParentDepth,
    getTopLevelNodeId,
    isGroupNode,
    getLockedGroupIdSet,
    isNodeLockedByGroup,
} from './utils/graph';
import {
    getNextNodeImageSequenceNumber,
    normalizeNodeImageSequences,
    getRestoredGroupNodeNumber,
} from './utils/imageSequence';
import {
    buildActiveGroupExecutionMetaMap,
} from './utils/execution';
import {
    buildCanvasBatchOps,
} from './utils/nodeData';
import {
    getProjectViewport,
    restoreNodeFromProject,
    restoreEdgesFromProject,
} from './utils/project';
import {
    syncConnectedTargetNodes,
} from './utils/validation';
import { useCanvasHistory } from './hooks/useCanvasHistory';
import { useCanvasExecution } from './hooks/useCanvasExecution';
import { useCanvasNodes } from './hooks/useCanvasNodes';
import { useCanvasClipboard } from './hooks/useCanvasClipboard';
import { useCanvasGroup } from './hooks/useCanvasGroup';
import { useCanvasViewport } from './hooks/useCanvasViewport';
import { useCanvasUpload } from './hooks/useCanvasUpload';
import { useCanvasTemplate } from './hooks/useCanvasTemplate';
import { useCanvasProject } from './hooks/useCanvasProject';
import { useCanvasInteraction } from './hooks/useCanvasInteraction';
import styles from './index.module.scss';
import '@xyflow/react/dist/style.css';
import { AppContext } from '@utils/AppContext';
import eventBus from '@utils/eventBus';
import LoggedHead from '@view/compoents/head/loggedHead/loggedHead';

const FreeCanvasInner = () => {
    const { member, globalUser, globalOrgInfo } = useContext(AppContext);

    // FreeCanvas 隐藏了全局 header，需要在此自行初始化 globalUser，
    // 否则 loadProjectList 因 resolvedProjectMember 为空而始终跳过请求。
    useEffect(() => {
        if (globalUser) return;
        // 用 setTimeout 0 确保 App.js 的 eventBus 监听已完成注册
        const timer = setTimeout(() => {
            eventBus.emit('updateUserInfo');
        }, 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const targetProjectId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_PROJECT_ID_QUERY_KEY) || '';
    }, [location.search]);
    const targetProjectName = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_PROJECT_NAME_QUERY_KEY) || '';
    }, [location.search]);
    const targetExecutionId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_EXECUTION_ID_QUERY_KEY) || '';
    }, [location.search]);
    const isPmView = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(PM_VIEW_QUERY_KEY) === '1';
    }, [location.search]);
    const forcePersonalMember = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(FORCE_PERSONAL_MEMBER_QUERY_KEY) === '1';
    }, [location.search]);
    const sourcePage = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(SOURCE_PAGE_QUERY_KEY) || '';
    }, [location.search]);
    const sourceTab = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(SOURCE_TAB_QUERY_KEY) || '';
    }, [location.search]);
    const returnToPath = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(RETURN_TO_QUERY_KEY) || '';
    }, [location.search]);
    const returnToStatePath = useMemo(
        () => String(location.state?.returnTo || ''),
        [location.state],
    );
    const returnToStateTab = useMemo(
        () => String(location.state?.tab || ''),
        [location.state],
    );
    const skipAutoSelect = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(SKIP_AUTO_SELECT_QUERY_KEY) === '1' || !!targetProjectId;
    }, [location.search, targetProjectId]);
    const createProjectRequested = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(CREATE_PROJECT_QUERY_KEY) === '1';
    }, [location.search]);
    const canvasRef = useRef(null);
    const reactFlowRef = useRef(null);
    const nodesRef = useRef([]);
    const edgesRef = useRef([]);
    const nextImageNodeNumberRef = useRef(1);
    const nextGroupNodeNumberRef = useRef(1);
    const historyRef = useRef([]);
    const redoHistoryRef = useRef([]);
    const clipboardRef = useRef(null);
    const pasteCountRef = useRef(0);
    const dragSnapshotRef = useRef(null);
    const manualAddCountRef = useRef(0);
    const batchUpdateQueueRef = useRef(Promise.resolve());
    const pendingFunctionSwitchRef = useRef(new Map());
    const projectListLoadingRef = useRef(false);
    const projectPageIndexRef = useRef(1);
    const projectKeywordRef = useRef('');
    const projectRequestIdRef = useRef(0);
    const projectDetailRequestIdRef = useRef(0);
    const projectVersionMapRef = useRef({});
    const currentProjectVersionRef = useRef(0);
    const activeProjectRef = useRef(null);
    const storedLastActiveProjectRef = useRef(getStoredLastActiveProject());
    const lastActiveProjectIdRef = useRef(storedLastActiveProjectRef.current.projectId || '');
    const lastActiveProjectPageIndexRef = useRef(storedLastActiveProjectRef.current.pageIndex || 1);
    const hasHydratedLastActiveProjectRef = useRef(false);
    const createProjectHandledRef = useRef(false);
    const creativeBootstrapHandledRef = useRef(false);
    const skipProjectDetailLoadRef = useRef('');
    const targetProjectHandledRef = useRef('');
    const targetProjectLocateTokenRef = useRef(0);
    const targetExecutionHandledRef = useRef('');
    const [activeId, setActiveId] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [projects, setProjects] = useState([]);
    const [projectListLoading, setProjectListLoading] = useState(false);
    const [projectListHasMore, setProjectListHasMore] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [materialLibraryOpen, setMaterialLibraryOpen] = useState(false);
    const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
    const [zoom, setZoom] = useState(100);
    const [viewportInteracting, setViewportInteracting] = useState(false);
    const [activeTool, setActiveTool] = useState('select');
    const [isSpacePressed, setIsSpacePressed] = useState(false);
    const [nextImageNodeNumber, setNextImageNodeNumber] = useState(1);
    const [nextGroupNodeNumber, setNextGroupNodeNumber] = useState(1);
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [runGroupSkipSelect, setRunGroupSkipSelect] = useState(() => {
        if (typeof window === 'undefined') return true;

        const rawValue = window.localStorage.getItem(RUN_GROUP_SKIP_SELECT_STORAGE_KEY);
        if (rawValue !== 'true' && rawValue !== 'false') {
            window.localStorage.setItem(RUN_GROUP_SKIP_SELECT_STORAGE_KEY, 'true');
            return true;
        }
        return rawValue !== 'false';
    });
    const [panelDismissToken, setPanelDismissToken] = useState(0);
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [contextMenu, setContextMenu] = useState({
        open: false,
        scope: 'pane',
        x: 0,
        y: 0,
        nodeId: null,
        flowPosition: null,
    });
    const [connectionAddMenu, setConnectionAddMenu] = useState({
        open: false,
        x: 0,
        y: 0,
        sourceNodeId: null,
        flowPosition: null,
    });
    const [toolbarLoadingKey, setToolbarLoadingKey] = useState('');

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(RUN_GROUP_SKIP_SELECT_STORAGE_KEY, String(runGroupSkipSelect));
    }, [runGroupSkipSelect]);

    useEffect(() => {
        if (!isPmView) return;
        setActiveTool('drag');
    }, [isPmView]);

    useEffect(() => {
        const handleRechargeOpen = () => {
            setPanelDismissToken((prev) => prev + 1);
            setContextMenu((prev) => ({ ...prev, open: false }));
            setHistoryModalOpen(false);
            setMaterialLibraryOpen(false);
            setTemplateLibraryOpen(false);
        };

        eventBus.on('recharge', handleRechargeOpen);
        return () => {
            eventBus.off('recharge', handleRechargeOpen);
        };
    }, []);

    // 获取当前激活项目
    const activeProject = useMemo(() => projects.find((project) => project.id === activeId), [projects, activeId]);
    const canvasEmpty = nodes.length === 0;
    const selectedNodeCount = selectedNodeIds.length;
    // 统计当前处于执行锁定中的分组
    const lockedGroupIds = useMemo(() => getLockedGroupIdSet(nodes), [nodes]);
    const isCanvasExecutionLocked = lockedGroupIds.size > 0;
    // 判断分组是否处于执行锁定状态
    const isGroupLocked = useCallback((groupId) => (
        !!groupId && lockedGroupIds.has(String(groupId))
    ), [lockedGroupIds]);
    // 判断节点是否因所属分组执行而被锁定
    const isNodeLocked = useCallback((nodeId) => {
        const node = nodesRef.current.find((item) => item.id === nodeId);
        return isNodeLockedByGroup(node, lockedGroupIds);
    }, [lockedGroupIds]);
    // 判断节点当前是否允许删除
    const isNodeDeletable = useCallback((nodeId) => {
        const node = nodesRef.current.find((item) => item.id === nodeId);
        if (!node) return false;

        return isGroupNode(node) || !isNodeLockedByGroup(node, lockedGroupIds);
    }, [lockedGroupIds]);
    // 批量判断节点中是否存在锁定项
    const areNodesLocked = useCallback((nodeIds) => (
        (nodeIds || []).some((nodeId) => isNodeLocked(nodeId))
    ), [isNodeLocked]);
    // 连线两端任一节点被锁定时，当前连线也不可编辑
    const isEdgeLocked = useCallback((edgeId) => {
        const edge = edgesRef.current.find((item) => item.id === edgeId);
        if (!edge) return false;

        return isNodeLocked(edge.source) || isNodeLocked(edge.target);
    }, [isNodeLocked]);

    useOnSelectionChange({
        onChange: ({ nodes: selectedNodes = [] }) => {
            setSelectedNodeIds(selectedNodes.map((node) => node.id));
        },
    });

    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    useEffect(() => {
        edgesRef.current = edges;
    }, [edges]);

    useEffect(() => {
        nextImageNodeNumberRef.current = nextImageNodeNumber;
    }, [nextImageNodeNumber]);

    useEffect(() => {
        nextGroupNodeNumberRef.current = nextGroupNodeNumber;
    }, [nextGroupNodeNumber]);

    useEffect(() => {
        projectListLoadingRef.current = projectListLoading;
    }, [projectListLoading]);

    useEffect(() => {
        projectVersionMapRef.current = projects.reduce((acc, project) => ({
            ...acc,
            [project.id]: Math.max(
                getVersionNumber(acc[project.id]),
                getVersionNumber(project.version),
            ),
        }), { ...projectVersionMapRef.current });
    }, [projects]);

    useEffect(() => {
        activeProjectRef.current = activeProject || null;
        if (activeProject) {
            currentProjectVersionRef.current = Math.max(
                getVersionNumber(currentProjectVersionRef.current),
                getVersionNumber(activeProject.version),
            );
        }
    }, [activeProject]);

    // 同步项目版本号，避免后续增量保存版本落后
    const syncProjectVersion = useCallback((projectId, version) => {
        const normalizedVersion = getVersionNumber(version);
        const currentVersion = getVersionNumber(projectVersionMapRef.current[projectId]);
        const nextVersion = Math.max(currentVersion, normalizedVersion);

        projectVersionMapRef.current = {
            ...projectVersionMapRef.current,
            [projectId]: nextVersion,
        };
        if (projectId === activeId) {
            currentProjectVersionRef.current = Math.max(
                getVersionNumber(currentProjectVersionRef.current),
                nextVersion,
            );
        }
        setProjects((prev) => prev.map((project) => (
            project.id === projectId
                ? { ...project, version: Math.max(getVersionNumber(project.version), nextVersion) }
                : project
        )));
    }, [activeId]);

    // 获取当前项目可用于保存的最新版本号
    const getCurrentProjectVersion = useCallback((projectId) => {
        if (projectId === activeId) {
            return Number(currentProjectVersionRef.current ?? 0);
        }

        const versionFromDetail = getVersionNumber(activeProjectRef.current?.id === projectId
            ? activeProjectRef.current?.version
            : 0);
        const versionFromList = getVersionNumber(projectVersionMapRef.current[projectId]);

        return Math.max(versionFromDetail, versionFromList, 0);
    }, [activeId]);

    // 将本次画布变更批量持久化到后端
    const persistCanvasBatch = useCallback(({
        beforeNodes = nodesRef.current,
        beforeEdges = edgesRef.current,
        afterNodes = nodesRef.current,
        afterEdges = edgesRef.current,
        beforeViewport = viewportRef.current,
        afterViewport = viewportRef.current,
        projectId = activeId,
    } = {}) => {
        if (!projectId) return Promise.resolve(null);

        const { nodeOps, edgeOps } = buildCanvasBatchOps({
            beforeNodes,
            beforeEdges,
            afterNodes,
            afterEdges,
            beforeViewport,
            afterViewport,
        });

        if (nodeOps.length === 0 && edgeOps.length === 0) {
            return Promise.resolve(null);
        }

        batchUpdateQueueRef.current = batchUpdateQueueRef.current
            .catch(() => null)
            .then(async () => {
                const submitBatchUpdate = (version) => batchUpdateFlowCanvas({
                    projectId,
                    version,
                    nodeOps,
                    edgeOps,
                });
                let result;

                try {
                    result = await submitBatchUpdate(getCurrentProjectVersion(projectId));
                } catch (error) {
                    if (!isCanvasVersionConflictError(error)) {
                        throw error;
                    }

                    const latestProjectDetail = await getFlowProjectDetail(projectId);
                    const latestVersion = getVersionNumber(latestProjectDetail?.version);
                    syncProjectVersion(projectId, latestVersion);
                    result = await submitBatchUpdate(latestVersion);
                }

                const nextVersion = getVersionNumber(
                    result?.version
                    ?? result?.canvasVersion
                    ?? result?.projectVersion
                    ?? (getCurrentProjectVersion(projectId) + 1),
                );

                syncProjectVersion(projectId, nextVersion);
                persistedViewportRef.current = { ...afterViewport };
                return result;
            })
            .catch((error) => {
                console.error(error);
                message.error(
                    isCanvasVersionConflictError(error)
                        ? CANVAS_VERSION_CONFLICT_MESSAGE
                        : getErrorMessage(error, '更新画布失败'),
                );
                return null;
            });

        return batchUpdateQueueRef.current;
        // viewportRef/persistedViewportRef 为稳定 ref，无需列入依赖
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId, getCurrentProjectVersion, syncProjectVersion]);

    // 关闭右键菜单
    const closeContextMenu = useCallback(() => {
        setContextMenu((prev) => ({ ...prev, open: false }));
    }, []);

    const closeConnectionAddMenu = useCallback(() => {
        setConnectionAddMenu((prev) => ({ ...prev, open: false }));
    }, []);

    // 视口管理
    const viewport = useCanvasViewport({
        canvasRef,
        reactFlowRef,
        nodesRef,
        edgesRef,
        setZoom,
        setViewportInteracting,
        isPmView,
        activeId,
        persistCanvasBatch,
        canvasEmpty,
        nodes,
    });
    const {
        viewportRef,
        persistedViewportRef,
        viewportPersistTimerRef,
        resetCanvasViewport,
        scheduleViewportPersist,
        handleZoomChange,
        handleMove,
        handleMoveEnd,
        handleFitCanvas,
        handleInit,
    } = viewport;

    // 执行与轮询管理
    const execution = useCanvasExecution({
        activeId,
        setNodes,
        setSelectedNodeIds,
        nodesRef,
        edgesRef,
        reactFlowRef,
        persistCanvasBatch,
        syncProjectVersion,
        isGroupLocked,
        activeGroupId,
        runGroupSkipSelect,
    });
    const {
        executionPollerRef,
        executionRequestAbortRef,
        executionPollFailureCountRef,
        pageActiveRef,
        stopAllExecutionPolling,
        pollNodeExecution,
        handleExecuteNode,
        pollGroupExecution,
        handleRunGroup,
        focusNodeByExecutionId,
    } = execution;

    // 页面激活状态与轮询/视口定时器清理
    useEffect(() => {
        pageActiveRef.current = true;
        const viewportPersistTimer = viewportPersistTimerRef.current;

        return () => {
            pageActiveRef.current = false;
            if (viewportPersistTimer) {
                clearTimeout(viewportPersistTimer);
            }
            Object.values(executionPollerRef.current).forEach((timer) => clearTimeout(timer));
            executionPollerRef.current = {};
            Object.values(executionRequestAbortRef.current).forEach((controller) => controller?.abort?.());
            executionRequestAbortRef.current = {};
            executionPollFailureCountRef.current = {};
        };
    }, [executionPollFailureCountRef, executionPollerRef, executionRequestAbortRef, pageActiveRef, viewportPersistTimerRef]);

    // 切换项目或新建项目时重置本地画布状态
    const resetCanvasState = useCallback(() => {
        setNodes([]);
        setEdges([]);
        setActiveGroupId(null);
        setSelectedNodeIds([]);
        setContextMenu({
            open: false,
            scope: 'pane',
            x: 0,
            y: 0,
            nodeId: null,
            flowPosition: null,
        });
        setConnectionAddMenu({
            open: false,
            x: 0,
            y: 0,
            sourceNodeId: null,
            flowPosition: null,
        });
        setNextImageNodeNumber(1);
        setNextGroupNodeNumber(1);
        nextImageNodeNumberRef.current = 1;
        nextGroupNodeNumberRef.current = 1;
        currentProjectVersionRef.current = 0;
        manualAddCountRef.current = 0;
        pasteCountRef.current = 0;
        clipboardRef.current = null;
        historyRef.current = [];
        redoHistoryRef.current = [];
        dragSnapshotRef.current = null;
        batchUpdateQueueRef.current = Promise.resolve();
        stopAllExecutionPolling();
        resetCanvasViewport();
    }, [resetCanvasViewport, setEdges, setNodes, stopAllExecutionPolling]);

    // 根据项目详情恢复整张画布
    const restoreProjectCanvas = useCallback((projectDetail) => {
        const projectNodes = Array.isArray(projectDetail?.nodes) ? projectDetail.nodes : [];
        const activeGroupExecutionMetaMap = buildActiveGroupExecutionMetaMap(projectNodes);
        const restoredBaseNodes = normalizeNodeImageSequences(sortNodesByParentDepth(
            projectNodes.map((node) => restoreNodeFromProject(node)),
        ));
        const nodeMap = new Map(projectNodes.map((node) => [String(node?.id ?? ''), node]));
        const restoredEdges = restoreEdgesFromProject(projectDetail?.edges || [], nodeMap);
        const restoredNodes = syncConnectedTargetNodes(restoredBaseNodes, restoredEdges).map((node) => {
            const nodeId = String(node.id || '');
            const parentId = String(node.parentId || '');
            const activeGroupMeta = activeGroupExecutionMetaMap.get(nodeId) || activeGroupExecutionMetaMap.get(parentId);

            if (!activeGroupMeta) return node;

            const isGroupNode = String(node.type || '') === 'groupNode';
            const currentExecutionId = String(node.data?.latestExecution?.executionId || '');
            const isFunctionNode = String(node.type || '') === 'functionNode';
            const isStaleExecutionResult = (
                isFunctionNode
                && currentExecutionId
                && currentExecutionId !== String(activeGroupMeta.executionId)
            );

            return {
                ...node,
                data: {
                    ...node.data,
                    groupExecutionLocked: true,
                    ...(isGroupNode
                        ? {}
                        : (
                            isStaleExecutionResult
                                ? {
                                    latestExecution: null,
                                    executionStatus: 'idle',
                                    activeExecutionId: '',
                                    selectedOutputId: '',
                                }
                                : {}
                        )),
                },
            };
        });
        const restoredViewport = getProjectViewport(projectDetail);

        setNodes(restoredNodes);
        setEdges(restoredEdges);
        setActiveGroupId(null);
        setSelectedNodeIds([]);
        setNextImageNodeNumber(getNextNodeImageSequenceNumber(restoredNodes));
        setNextGroupNodeNumber(getRestoredGroupNodeNumber(restoredNodes));
        nextImageNodeNumberRef.current = getNextNodeImageSequenceNumber(restoredNodes);
        nextGroupNodeNumberRef.current = getRestoredGroupNodeNumber(restoredNodes);
        manualAddCountRef.current = 0;
        pasteCountRef.current = 0;
        clipboardRef.current = null;
        historyRef.current = [];
        redoHistoryRef.current = [];
        dragSnapshotRef.current = null;
        batchUpdateQueueRef.current = Promise.resolve();
        stopAllExecutionPolling();
        viewportRef.current = restoredViewport || { ...DEFAULT_VIEWPORT };
        persistedViewportRef.current = restoredViewport || { ...DEFAULT_VIEWPORT };
        setContextMenu({
            open: false,
            scope: 'pane',
            x: 0,
            y: 0,
            nodeId: null,
            flowPosition: null,
        });
        setConnectionAddMenu({
            open: false,
            x: 0,
            y: 0,
            sourceNodeId: null,
            flowPosition: null,
        });
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!reactFlowRef.current) return;

                if (restoredViewport) {
                    reactFlowRef.current.setViewport(restoredViewport, { duration: 0 });
                    setZoom(Math.round(restoredViewport.zoom * 100));
                    return;
                }

                if (restoredNodes.length === 0) {
                    resetCanvasViewport();
                    return;
                }

                reactFlowRef.current.fitView({
                    duration: 0,
                    padding: 0.18,
                });
                setZoom(Math.round((reactFlowRef.current.getZoom?.() ?? 1) * 100));
            });
        });
    }, [persistedViewportRef, resetCanvasViewport, setEdges, setNodes, stopAllExecutionPolling, viewportRef]);

    // 撤销/重做历史管理
    const history = useCanvasHistory({
        nodesRef,
        edgesRef,
        nextImageNodeNumberRef,
        nextGroupNodeNumberRef,
        activeGroupId,
        selectedNodeIds,
        setNodes,
        setEdges,
        setNextImageNodeNumber,
        setNextGroupNodeNumber,
        setActiveGroupId,
        setSelectedNodeIds,
        persistCanvasBatch,
        pasteCountRef,
        historyRef,
        redoHistoryRef,
    });
    const {
        getSnapshot,
        pushHistory,
        handleUndo,
        handleRedo,
    } = history;

    // 项目管理
    const project = useCanvasProject({
        location,
        navigate,
        projects,
        setProjects,
        activeId,
        setActiveId,
        member,
        globalUser,
        globalOrgInfo,
        isPmView,
        forcePersonalMember,
        skipAutoSelect,
        createProjectRequested,
        targetProjectId,
        targetProjectName,
        targetExecutionId,
        nodes,
        edges,
        setSidebarCollapsed,
        setProjectListLoading,
        projectListHasMore,
        setProjectListHasMore,
        resetCanvasState,
        restoreProjectCanvas,
        pollNodeExecution,
        pollGroupExecution,
        focusNodeByExecutionId,
        stopAllExecutionPolling,
        syncProjectVersion,
        getCurrentProjectVersion,
        projectVersionMapRef,
        currentProjectVersionRef,
        activeProjectRef,
        projectListLoadingRef,
        projectPageIndexRef,
        projectKeywordRef,
        projectRequestIdRef,
        projectDetailRequestIdRef,
        lastActiveProjectIdRef,
        lastActiveProjectPageIndexRef,
        hasHydratedLastActiveProjectRef,
        createProjectHandledRef,
        skipProjectDetailLoadRef,
        targetProjectHandledRef,
        targetProjectLocateTokenRef,
        targetExecutionHandledRef,
    });
    const {
        loadProjectList,
        handleProjectKeywordChange,
        handleLoadMoreProjects,
        handleSelectProject,
        clearGuideQueryParams,
        handleCreate,
        handleNameChange,
        handleRenameProject,
        handleDuplicateProject,
        handlePinProject,
        handleSortProject,
        handleDeleteProject,
        ensureCanvasProject,
    } = project;

    // 节点/连线操作管理
    const nodesOps = useCanvasNodes({
        nodesRef,
        edgesRef,
        setNodes,
        setEdges,
        onEdgesChange,
        setSelectedNodeIds,
        nextImageNodeNumberRef,
        setNextImageNodeNumber,
        activeGroupId,
        setActiveGroupId,
        reactFlowRef,
        canvasRef,
        manualAddCountRef,
        setConnectionAddMenu,
        isNodeLocked,
        isEdgeLocked,
        isNodeDeletable,
        areNodesLocked,
        persistCanvasBatch,
        pushHistory,
        closeConnectionAddMenu,
        closeContextMenu,
        isPmView,
        activeTool,
        isCanvasExecutionLocked,
        pendingFunctionSwitchRef,
        handleExecuteNode,
        pollGroupExecution,
        ensureCanvasProject,
    });
    const {
        handleNodeDataChange,
        handleImageNodeSizeChange,
        handleSwitchFunctionNode,
        handleNodeGenerateComplete,
        handleNodeResultSelect,
        handleConnect,
        handleConnectEnd,
        handleAddNode,
        handleQuickAddNode,
        handleDeleteNode,
        handleDeleteSelection,
        handleEdgesChange,
        handleDeleteEdge,
        handleRemoveFunctionLinkedUpload,
        handleReplaceFunctionLinkedUpload,
        handleRemoveCreativeLinkedUpload,
        handleReplaceCreativeLinkedUpload,
        handleEdgeSlotChange,
        handleSwapFunctionInputSlots,
    } = nodesOps;

    // 剪贴板管理
    const clipboard = useCanvasClipboard({
        nodesRef,
        edgesRef,
        nextImageNodeNumberRef,
        setNodes,
        setEdges,
        setNextImageNodeNumber,
        pushHistory,
        persistCanvasBatch,
        areNodesLocked,
        activeId,
        clipboardRef,
        pasteCountRef,
    });
    const {
        buildClipboardPayloadFromState,
        cloneClipboardToCanvas,
        handleCopy,
        handlePaste,
        handleCopyNode,
        handleDuplicateNode,
        handleDuplicateSelection,
        getDragDuplicateSourceNodeIds,
        handleCopyTaskId,
    } = clipboard;

    // 分组操作管理
    const groupOps = useCanvasGroup({
        nodesRef,
        edgesRef,
        selectedNodeIds,
        activeGroupId,
        setActiveGroupId,
        setNodes,
        setNextGroupNodeNumber,
        nextGroupNodeNumberRef,
        persistCanvasBatch,
        pushHistory,
        areNodesLocked,
        isGroupLocked,
        setToolbarLoadingKey,
    });
    const {
        getSelectedGroupNodes,
        getSelectedGroupableNodes,
        getSelectedAppendableGroup,
        handleGroupSelection,
        handleUngroupSelection,
        handleBatchDownloadGroup,
        handleBatchDownloadSelection,
    } = groupOps;

    // 上传管理
    const upload = useCanvasUpload({
        reactFlowRef,
        canvasRef,
        nodesRef,
        edgesRef,
        setNodes,
        setNextImageNodeNumber,
        nextImageNodeNumberRef,
        ensureCanvasProject,
        pushHistory,
        persistCanvasBatch,
        handleAddNode,
        isPmView,
    });
    const {
        uploadInputRef: canvasUploadInputRef,
        canvasUploadCount: uploadCount,
        handleUploadFiles,
        handleCanvasDragOver,
        handleCanvasDrop,
        handleSendHistoryImage,
        handleSelectCanvasExample,
        handleBatchSendHistoryImages,
        handleSelectMaterialLibraryImage,
    } = upload;

    // 模板管理
    const template = useCanvasTemplate({
        nodesRef,
        edgesRef,
        reactFlowRef,
        canvasRef,
        activeId,
        setProjects,
        ensureCanvasProject,
        cloneClipboardToCanvas,
        setTemplateLibraryOpen,
        getCurrentProjectVersion,
        loadProjectList,
        projectKeywordRef,
        syncProjectVersion,
        restoreProjectCanvas,
        clearGuideQueryParams,
        globalUser,
        member,
    });
    const {
        saveTemplateModalOpen: templateModalOpen,
        saveTemplateName: templateName,
        setSaveTemplateName: setTemplateName,
        publishTemplateSync: templatePublishSync,
        setPublishTemplateSync: setTemplatePublishSync,
        saveTemplateSubmitting: templateSubmitting,
        handleOpenSaveTemplateModal,
        handleCloseSaveTemplateModal,
        handleSaveTemplate,
        handleApplyTemplateToCanvas,
    } = template;

    // 创意节点引导（从 CreativeToImage 跳转时自动创建节点）
    useEffect(() => {
        if (isPmView || !activeId || nodes.length > 0 || edges.length > 0) return;
        if (creativeBootstrapHandledRef.current) return;
        if (!canvasRef.current || !reactFlowRef.current) return;

        const rawPayload = window.sessionStorage.getItem(FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY);
        if (!rawPayload) return;

        let payload = null;
        try {
            payload = normalizeCreativeBootstrapPayload(JSON.parse(rawPayload));
        } catch (error) {
            payload = null;
        }

        if (!payload) {
            window.sessionStorage.removeItem(FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY);
            return;
        }

        creativeBootstrapHandledRef.current = true;

        const bootstrapCreativeNode = async () => {
            try {
                const result = await handleAddNode('creativeImg2Img', {
                    autoOpenFunctionPanel: true,
                    dataPatch: buildCreativeBootstrapNodePatch(payload),
                });

                window.sessionStorage.removeItem(FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY);

                if (payload.shouldAutoGenerate && result?.nodeId) {
                    await handleExecuteNode(String(result.nodeId));
                }
            } catch (error) {
                console.error(error);
                creativeBootstrapHandledRef.current = false;
            }
        };

        bootstrapCreativeNode();
    }, [activeId, edges.length, handleAddNode, handleExecuteNode, isPmView, nodes.length]);

    // 交互管理（工具切换、右键菜单、拖拽、快捷键、浮层）
    const interaction = useCanvasInteraction({
        isPmView,
        activeTool,
        setActiveTool,
        isSpacePressed,
        setIsSpacePressed,
        canvasEmpty,
        zoom,
        setZoom,
        nodes,
        nodesRef,
        edgesRef,
        reactFlowRef,
        canvasRef,
        activeGroupId,
        setActiveGroupId,
        setNodes,
        setSidebarCollapsed,
        setPanelDismissToken,
        selectedNodeIds,
        contextMenu,
        setContextMenu,
        connectionAddMenu,
        setConnectionAddMenu,
        closeContextMenu,
        closeConnectionAddMenu,
        clipboardRef,
        pasteCountRef,
        toolbarLoadingKey,
        runGroupSkipSelect,
        setRunGroupSkipSelect,
        uploadInputRef: canvasUploadInputRef,
        setHistoryModalOpen,
        setMaterialLibraryOpen,
        setTemplateLibraryOpen,
        handleZoomChange,
        handleCopy,
        handlePaste,
        handleUndo,
        handleRedo,
        handleGroupSelection,
        handleUngroupSelection,
        handleDeleteSelection,
        handleAddNode,
        handleQuickAddNode,
        handleCopyNode,
        handleDuplicateNode,
        handleDeleteNode,
        handleCopyTaskId,
        handleRunGroup,
        handleBatchDownloadGroup,
        handleBatchDownloadSelection,
        handleDuplicateSelection,
        handleOpenSaveTemplateModal,
        isNodeDeletable,
        isNodeLocked,
        areNodesLocked,
        isGroupLocked,
        getSelectedGroupNodes,
        getSelectedGroupableNodes,
        getSelectedAppendableGroup,
        getSnapshot,
        buildClipboardPayloadFromState,
        cloneClipboardToCanvas,
        getDragDuplicateSourceNodeIds,
        pushHistory,
        persistCanvasBatch,
        scheduleViewportPersist,
        navigate,
        returnToStatePath,
        returnToStateTab,
        sourcePage,
        sourceTab,
        returnToPath,
        dragSnapshotRef,
    });
    const {
        handleAction,
        handleToolChange,
        handleNodeContextMenu,
        handleNodeClick,
        handlePaneContextMenu,
        handlePaneClick,
        handleNodeDragStart,
        handleNodeDragStop,
        handleBack,
        contextMenuItems,
        connectionAddMenuItems,
        groupActionToolbar,
    } = interaction;

    // 注册画布节点类型
    const nodeTypes = useMemo(() => ({
        imageNode: ImageNode,
        functionNode: FunctionNode,
        groupNode: GroupNode,
        pendingConnectionNode: PendingConnectionNode,
    }), []);

    // 注册画布连线类型
    const edgeTypes = useMemo(() => ({
        flowEdge: FlowEdge,
    }), []);

    // 设置默认连线配置
    const defaultEdgeOptions = useMemo(() => ({
        type: 'flowEdge',
    }), []);

    // 给节点注入渲染层需要的事件和状态
    const nodesWithHandlers = useMemo(() => {
        const nodeMap = new Map(nodes.map((node) => [String(node.id), node]));
        const topLevelOrderMap = new Map();
        const topLevelNodes = nodes.filter((node) => !node.parentId);
        const selectedTopLevelNodeIds = new Set(
            topLevelNodes
                .filter((node) => !!node.selected)
                .map((node) => String(node.id)),
        );

        topLevelNodes.forEach((node, index) => {
            const nodeId = String(node.id);
            const baseOrder = index;
            const elevatedOrder = selectedTopLevelNodeIds.has(nodeId)
                ? topLevelNodes.length + index
                : baseOrder;
            topLevelOrderMap.set(nodeId, elevatedOrder);
        });

        return nodes.map((node) => {
            const topLevelNodeId = getTopLevelNodeId(node, nodeMap);
            const topLevelOrder = topLevelOrderMap.get(topLevelNodeId) ?? 0;
            const zIndexBase = topLevelOrder * 10;

            return {
                ...node,
                zIndex: zIndexBase + (node.type === 'groupNode' ? 0 : 4),
                style: {
                    ...(node.style || {}),
                    zIndex: zIndexBase + (node.type === 'groupNode' ? 0 : 4),
                },
                draggable: node.type === 'groupNode'
                    ? (!isPmView && activeTool === 'select')
                    : (!isPmView && activeTool === 'select' && !isNodeLockedByGroup(node, lockedGroupIds)),
                data: {
                    ...node.data,
                    onChange: handleNodeDataChange,
                    onImageSizeChange: handleImageNodeSizeChange,
                    onSwitchFunction: handleSwitchFunctionNode,
                    onExecute: handleExecuteNode,
                    onResultSelect: handleNodeResultSelect,
                    onQuickAddNode: handleQuickAddNode,
                    onGenerateComplete: handleNodeGenerateComplete,
                    onRemoveLinkedUpload: handleRemoveCreativeLinkedUpload,
                    onReplaceLinkedUpload: handleReplaceCreativeLinkedUpload,
                    onRemoveLinkedInput: handleRemoveFunctionLinkedUpload,
                    onReplaceLinkedInput: handleReplaceFunctionLinkedUpload,
                    onSwapInputSlots: handleSwapFunctionInputSlots,
                    panelDismissToken,
                    projectId: activeId,
                    selectedNodeCount,
                    viewportInteracting,
                    interactionLocked: isPmView || isNodeLockedByGroup(node, lockedGroupIds),
                    readOnlyView: isPmView,
                    groupExecutionLocked: (
                        !!node.data?.groupExecutionLocked
                        || (!!node.parentId && lockedGroupIds.has(String(node.parentId)))
                    ),
                },
            };
        });
    }, [activeId, activeTool, handleExecuteNode, handleImageNodeSizeChange, handleNodeDataChange, handleNodeGenerateComplete, handleNodeResultSelect, handleQuickAddNode, handleRemoveCreativeLinkedUpload, handleRemoveFunctionLinkedUpload, handleReplaceCreativeLinkedUpload, handleReplaceFunctionLinkedUpload, handleSwapFunctionInputSlots, handleSwitchFunctionNode, isPmView, lockedGroupIds, nodes, panelDismissToken, selectedNodeCount, viewportInteracting]);

    // 给连线注入渲染层需要的事件和状态
    const edgesWithHandlers = useMemo(() => edges.map((edge) => ({
        ...edge,
        data: {
            ...edge.data,
            onDeleteEdge: handleDeleteEdge,
            onSlotChange: handleEdgeSlotChange,
            interactionLocked: isPmView || isNodeLocked(edge.source) || isNodeLocked(edge.target),
        },
    })), [edges, handleDeleteEdge, handleEdgeSlotChange, isNodeLocked, isPmView]);

    const pendingConnectionVisible = (
        connectionAddMenu.open
        && !!connectionAddMenu.sourceNodeId
        && !!connectionAddMenu.flowPosition
        && nodes.some((node) => node.id === connectionAddMenu.sourceNodeId)
    );

    const renderedNodes = useMemo(() => {
        if (!pendingConnectionVisible) return nodesWithHandlers;

        return nodesWithHandlers.concat({
            id: PENDING_CONNECTION_NODE_ID,
            type: 'pendingConnectionNode',
            position: {
                x: connectionAddMenu.flowPosition.x - 22,
                y: connectionAddMenu.flowPosition.y - 0.5,
            },
            width: 1,
            height: 1,
            style: {
                width: 1,
                height: 1,
                pointerEvents: 'none',
            },
            data: {},
            draggable: false,
            selectable: false,
            connectable: false,
            focusable: false,
        });
    }, [connectionAddMenu.flowPosition, nodesWithHandlers, pendingConnectionVisible]);

    const renderedEdges = useMemo(() => {
        if (!pendingConnectionVisible) return edgesWithHandlers;

        return edgesWithHandlers.concat({
            id: PENDING_CONNECTION_EDGE_ID,
            source: connectionAddMenu.sourceNodeId,
            target: PENDING_CONNECTION_NODE_ID,
            type: 'flowEdge',
            selected: false,
            selectable: false,
            focusable: false,
            zIndex: 8,
            data: {
                pending: true,
                interactionLocked: true,
            },
        });
    }, [connectionAddMenu.sourceNodeId, edgesWithHandlers, pendingConnectionVisible]);

    return (
        <div className={styles.container}>
            <ProjectHeader
                name={activeProject?.name || targetProjectName || DEFAULT_CANVAS_PROJECT_NAME}
                onNameChange={handleNameChange}
                onBack={handleBack}
                onOpenProjectList={() => setSidebarCollapsed(false)}
                onCreateProject={handleCreate}
                sidebarCollapsed={sidebarCollapsed}
                editable={!isPmView}
                createDisabled={isPmView}
            />
            <div className={styles.loggedHeadWrap}>
                <LoggedHead showPromoteButtons={false} />
            </div>
            <ZoomToolbar
                zoom={zoom}
                onZoomChange={handleZoomChange}
                onFitCanvas={handleFitCanvas}
            />
            {!isPmView ? (
                <CanvasToolbar
                    activeTool={activeTool}
                    onToolChange={handleToolChange}
                    onAction={handleAction}
                    onAddNode={(nodeType) => handleAddNode(nodeType, { autoOpenFunctionPanel: true })}
                />
            ) : null}
            <input
                ref={canvasUploadInputRef}
                type="file"
                accept={UPLOAD_IMAGE_ACCEPT}
                multiple
                onChange={handleUploadFiles}
                style={{ display: 'none' }}
            />
            <Sidebar
                projects={projects}
                activeId={activeId}
                keyword={projectKeywordRef.current}
                onSelect={handleSelectProject}
                onCreate={handleCreate}
                onKeywordChange={handleProjectKeywordChange}
                onLoadMore={handleLoadMoreProjects}
                onPinProject={handlePinProject}
                onDuplicateProject={handleDuplicateProject}
                onSortProject={handleSortProject}
                onRenameProject={handleRenameProject}
                onDeleteProject={handleDeleteProject}
                hasMore={projectListHasMore}
                loading={projectListLoading}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
                readOnlyView={isPmView}
            />
            <div
                ref={canvasRef}
                className={styles.canvas}
                onDragOver={handleCanvasDragOver}
                onDrop={handleCanvasDrop}
            >
                <div className={`${styles.reactFlow} ${activeTool === 'drag' ? styles.dragMode : styles.selectMode} ${isSpacePressed ? styles.spaceMode : ''}`}>
                      <ReactFlow
                        nodes={renderedNodes}
                        edges={renderedEdges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        defaultEdgeOptions={defaultEdgeOptions}
                        onNodesChange={onNodesChange}
                        onEdgesChange={handleEdgesChange}
                        onConnect={handleConnect}
                        onConnectEnd={handleConnectEnd}
                        onMove={handleMove}
                        onMoveEnd={handleMoveEnd}
                        onInit={handleInit}
                        onNodeClick={handleNodeClick}
                        onNodeContextMenu={handleNodeContextMenu}
                        onPaneClick={handlePaneClick}
                        onPaneContextMenu={handlePaneContextMenu}
                        onNodeDragStart={handleNodeDragStart}
                        onNodeDragStop={handleNodeDragStop}
                        nodesDraggable={!isPmView && activeTool === 'select'}
                        elementsSelectable={!isPmView && activeTool === 'select'}
                        nodesConnectable={!isPmView && activeTool === 'select' && !isCanvasExecutionLocked}
                        elevateNodesOnSelect={false}
                        elevateEdgesOnSelect={false}
                        deleteKeyCode={null}
                        panOnDrag={isPmView || activeTool === 'drag'}
                        zoomOnScroll={false}
                        zoomOnPinch={false}
                        zoomActivationKeyCode={null}
                        panOnScroll
                        panOnScrollSpeed={0.5}
                        preventScrolling
                        selectionOnDrag={!isPmView && activeTool === 'select'}
                        selectionMode="partial"
                        connectionRadius={28}
                        connectionLineType={ConnectionLineType.Bezier}
                        connectionLineStyle={{ stroke: '#7D8DA1', strokeWidth: 2.5, opacity: 1 }}
                        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                        minZoom={0.1}
                        maxZoom={2}
                        fitView={false}
                        proOptions={{ hideAttribution: true }}
                        className={styles.reactFlowCanvas}
                    >
                        <GroupActionToolbar
                            visible={groupActionToolbar.visible}
                            nodeIds={groupActionToolbar.nodeIds}
                            position={groupActionToolbar.position}
                            items={groupActionToolbar.items}
                        />
                        <Background
                            variant={BackgroundVariant.Dots}
                            gap={20}
                            size={1}
                            color="#DDDFE3"
                        />
                    </ReactFlow>
                </div>
                {uploadCount > 0 ? (
                    <div className={styles.canvasUploadLoading} aria-label="图片上传中">
                        <LoadingOutlined spin />
                        <span>上传中</span>
                    </div>
                ) : null}
                <CanvasContextMenu
                    open={!isPmView && contextMenu.open}
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenuItems}
                    onClose={closeContextMenu}
                />
                <CanvasContextMenu
                    open={!isPmView && connectionAddMenu.open}
                    x={connectionAddMenu.x}
                    y={connectionAddMenu.y}
                    items={connectionAddMenuItems}
                    onClose={closeConnectionAddMenu}
                />
                {canvasEmpty && !isPmView ? (
                    <CanvasEmpty
                        onSelectExample={handleSelectCanvasExample}
                        onAddNode={handleAddNode}
                    />
                ) : null}
            </div>
            <GenerationHistoryModal
                open={historyModalOpen}
                projectId={activeId}
                onClose={() => setHistoryModalOpen(false)}
                onSend={handleSendHistoryImage}
                onBatchSend={handleBatchSendHistoryImages}
            />
            <MaterialLibraryModal
                open={materialLibraryOpen}
                onClose={() => setMaterialLibraryOpen(false)}
                onSelect={handleSelectMaterialLibraryImage}
                multiple
                maxSelectCount={10}
                showApplyAction={false}
            />
            <TemplateLibraryModal
                open={templateLibraryOpen}
                onClose={() => setTemplateLibraryOpen(false)}
                onApply={handleApplyTemplateToCanvas}
            />
            <Modal
                open={templateModalOpen}
                title="存为模板"
                onCancel={handleCloseSaveTemplateModal}
                footer={null}
                width={470}
                centered
                destroyOnClose={false}
                closable={!templateSubmitting}
                maskClosable={!templateSubmitting}
                className={styles.saveTemplateModal}
            >
                <div className={styles.saveTemplateContent}>
                    <div className={styles.saveTemplateFormRow}>
                        <div className={styles.saveTemplateLabel}>模板名称</div>
                        <Input
                            value={templateName}
                            onChange={(event) => setTemplateName(event.target.value)}
                            placeholder="请填写模板名称"
                            maxLength={15}
                            className={styles.saveTemplateInput}
                        />
                    </div>
                    <Checkbox
                        checked={templatePublishSync}
                        onChange={(event) => setTemplatePublishSync(event.target.checked)}
                        className={styles.saveTemplateCheckbox}
                    >
                        同步发布到公共模版库
                    </Checkbox>
                    <div className={styles.saveTemplateActions}>
                        <Button
                            className={styles.saveTemplateCancel}
                            onClick={handleCloseSaveTemplateModal}
                            disabled={templateSubmitting}
                        >
                            取消
                        </Button>
                        <Button
                            type="primary"
                            className={styles.saveTemplateConfirm}
                            loading={templateSubmitting}
                            onClick={handleSaveTemplate}
                        >
                            保存
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const FreeCanvas = () => (
    <ReactFlowProvider>
        <FreeCanvasInner />
    </ReactFlowProvider>
);

export default FreeCanvas;